/**
 * Рисунки отделки стен, нарисованные кодом: полоска, ромб, дамаск, соты, рогожка, листья,
 * штукатурка. Каждый — бесшовная плитка в один раппорт: по ширине — раппорт, по высоте —
 * раппорт × `patternAspect` (соты не квадратные).
 *
 * Чистый canvas, без three.js: этим же рисует и текстуру стены в 3D (`finishTexture`), и
 * образец в каталоге «Отделки» (`finishPatternTile`) — на карточке тот же рисунок, что ляжет
 * на стену.
 */
import { WallPattern } from "@/shared/catalog";

/** Высота плитки в долях ширины — у сот ряд смещён, и бесшовная плитка выше, чем шире. */
export const patternAspect = (pattern: WallPattern): number =>
	pattern === WallPattern.Geometric ? Math.sqrt(3) : 1;

type Draw = (ctx: CanvasRenderingContext2D, w: number, h: number, accent: string) => void;

/** Детерминированный «случайный» ряд: одинаковый рисунок при каждой перерисовке. */
const seeded = (seed: number) => {
	let state = seed;
	return () => {
		state = (state * 1664525 + 1013904223) % 4294967296;
		return state / 4294967296;
	};
};

/** Рисует фигуру девять раз со сдвигом на плитку — так всё, что вылезло за край, входит с другой стороны. */
const wrapped = (w: number, h: number, draw: (dx: number, dy: number) => void) => {
	for (const dx of [-w, 0, w]) for (const dy of [-h, 0, h]) draw(dx, dy);
};

const stripes: Draw = (ctx, w, h, accent) => {
	ctx.fillStyle = accent;
	for (const [x, width] of [
		[0.04, 0.1],
		[0.2, 0.015],
		[0.26, 0.015],
		[0.5, 0.16],
		[0.76, 0.02],
	]) {
		ctx.fillRect(x * w, 0, width * w, h);
	}
};

const rhombus: Draw = (ctx, w, h, accent) => {
	ctx.strokeStyle = accent;
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.moveTo(0, 0);
	ctx.lineTo(w, h);
	ctx.moveTo(w, 0);
	ctx.lineTo(0, h);
	ctx.stroke();
	ctx.fillStyle = accent;
	ctx.beginPath();
	ctx.arc(w / 2, h / 2, 3, 0, Math.PI * 2);
	ctx.fill();
};

/** Медальон дамаска вокруг точки: вытянутый ромб, лепестки и завиток сверху и снизу. */
const medallion = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
	ctx.save();
	ctx.translate(x, y);
	ctx.beginPath();
	ctx.moveTo(0, -size);
	ctx.bezierCurveTo(size * 0.55, -size * 0.45, size * 0.55, size * 0.45, 0, size);
	ctx.bezierCurveTo(-size * 0.55, size * 0.45, -size * 0.55, -size * 0.45, 0, -size);
	ctx.fill();
	for (const angle of [0.9, -0.9, Math.PI - 0.9, Math.PI + 0.9]) {
		ctx.save();
		ctx.rotate(angle);
		ctx.beginPath();
		ctx.ellipse(0, -size * 0.62, size * 0.12, size * 0.32, 0, 0, Math.PI * 2);
		ctx.fill();
		ctx.restore();
	}
	ctx.restore();
};

const damask: Draw = (ctx, w, h, accent) => {
	ctx.fillStyle = accent;
	const size = w * 0.24;
	medallion(ctx, w / 2, h / 2, size);
	wrapped(w, h, (dx, dy) => medallion(ctx, dx, dy, size));
	// Мелкие ромбики между медальонами
	for (const [x, y] of [
		[w / 2, 0],
		[0, h / 2],
		[w / 2, h],
		[w, h / 2],
	]) {
		ctx.beginPath();
		ctx.moveTo(x, y - 6);
		ctx.lineTo(x + 4, y);
		ctx.lineTo(x, y + 6);
		ctx.lineTo(x - 4, y);
		ctx.fill();
	}
};

const geometric: Draw = (ctx, w, h, accent) => {
	// Шестиугольники «остриём вверх»: шаг по горизонтали w, ряды через 1,5 радиуса
	const r = w / Math.sqrt(3);
	ctx.strokeStyle = accent;
	ctx.lineWidth = 3;
	const hex = (cx: number, cy: number) => {
		ctx.beginPath();
		for (let i = 0; i < 6; i++) {
			const angle = Math.PI / 6 + (i * Math.PI) / 3;
			const x = cx + r * 0.96 * Math.cos(angle);
			const y = cy + r * 0.96 * Math.sin(angle);
			if (i === 0) ctx.moveTo(x, y);
			else ctx.lineTo(x, y);
		}
		ctx.closePath();
		ctx.stroke();
	};
	const rows = Math.ceil(h / (1.5 * r)) + 1;
	for (let row = -1; row <= rows; row++) {
		for (let col = -1; col <= 2; col++) {
			hex(col * w + (Math.abs(row) % 2 ? w / 2 : 0), row * 1.5 * r);
		}
	}
};

/**
 * Рогожка — плетение «корзинкой»: клетки по очереди из горизонтальных и вертикальных нитей.
 * Нити крупнее настоящих: тонкая сетка на расстоянии пары метров сливается в ровный цвет,
 * и обои выглядели бы как краска. Яркость нитей чуть гуляет — плетение не выглядит сеткой.
 */
const linen: Draw = (ctx, w, h, accent) => {
	const cells = 12;
	const cw = w / cells;
	const ch = h / cells;
	const threads = 3;
	ctx.fillStyle = accent;
	for (let i = 0; i < cells; i++) {
		for (let j = 0; j < cells; j++) {
			const horizontal = (i + j) % 2 === 0;
			for (let k = 0; k < threads; k++) {
				// Период разброса (4) делит число клеток — на стыке плиток нет шва
				ctx.globalAlpha = 0.45 + 0.45 * (((i * 5 + j * 3 + k) % 4) / 3);
				if (horizontal) {
					ctx.fillRect(i * cw, j * ch + (k + 0.15) * (ch / threads), cw, (ch / threads) * 0.6);
				} else {
					ctx.fillRect(i * cw + (k + 0.15) * (cw / threads), j * ch, (cw / threads) * 0.6, ch);
				}
			}
		}
	}
	ctx.globalAlpha = 1;
};

const botanical: Draw = (ctx, w, h, accent) => {
	const random = seeded(7);
	const leaf = (x: number, y: number, length: number, angle: number) => {
		ctx.save();
		ctx.translate(x, y);
		ctx.rotate(angle);
		ctx.fillStyle = accent;
		ctx.beginPath();
		ctx.moveTo(0, 0);
		ctx.bezierCurveTo(length * 0.35, -length * 0.3, length * 0.75, -length * 0.2, length, 0);
		ctx.bezierCurveTo(length * 0.75, length * 0.2, length * 0.35, length * 0.3, 0, 0);
		ctx.fill();
		ctx.globalAlpha = 0.5;
		ctx.strokeStyle = "#ffffff";
		ctx.lineWidth = 1.2;
		ctx.beginPath();
		ctx.moveTo(0, 0);
		ctx.lineTo(length * 0.95, 0);
		ctx.stroke();
		ctx.globalAlpha = 1;
		ctx.restore();
	};
	const leaves = Array.from({ length: 7 }, () => ({
		x: random() * w,
		y: random() * h,
		length: w * (0.18 + random() * 0.14),
		angle: random() * Math.PI * 2,
	}));
	for (const item of leaves)
		wrapped(w, h, (dx, dy) => leaf(item.x + dx, item.y + dy, item.length, item.angle));
};

const concrete: Draw = (ctx, w, h, accent) => {
	const random = seeded(19);
	// Крупные пятна — мягкими радиальными градиентами
	for (let i = 0; i < 26; i++) {
		const x = random() * w;
		const y = random() * h;
		const radius = w * (0.05 + random() * 0.12);
		const alpha = 0.12 + random() * 0.18;
		wrapped(w, h, (dx, dy) => {
			const gradient = ctx.createRadialGradient(x + dx, y + dy, 0, x + dx, y + dy, radius);
			gradient.addColorStop(0, accent);
			gradient.addColorStop(1, `${accent}00`);
			ctx.globalAlpha = alpha;
			ctx.fillStyle = gradient;
			ctx.fillRect(x + dx - radius, y + dy - radius, radius * 2, radius * 2);
		});
	}
	// Поры — редкие тёмные точки
	ctx.globalAlpha = 0.45;
	ctx.fillStyle = accent;
	for (let i = 0; i < 90; i++) {
		ctx.beginPath();
		ctx.arc(random() * w, random() * h, 0.6 + random() * 1.2, 0, Math.PI * 2);
		ctx.fill();
	}
	ctx.globalAlpha = 1;
};

const silk: Draw = (ctx, w, h, accent) => {
	const random = seeded(31);
	ctx.strokeStyle = accent;
	for (let i = 0; i < 22; i++) {
		const y0 = random() * h;
		const amplitude = h * (0.03 + random() * 0.06);
		const phase = random() * Math.PI * 2;
		ctx.globalAlpha = 0.18 + random() * 0.25;
		ctx.lineWidth = 3 + random() * 8;
		// Целое число волн на плитку — узор сходится по горизонтали
		const waves = 1 + Math.floor(random() * 2);
		wrapped(w, h, (_, dy) => {
			ctx.beginPath();
			for (let x = 0; x <= w; x += 4) {
				const y = y0 + dy + Math.sin((x / w) * Math.PI * 2 * waves + phase) * amplitude;
				if (x === 0) ctx.moveTo(x, y);
				else ctx.lineTo(x, y);
			}
			ctx.stroke();
		});
	}
	ctx.globalAlpha = 1;
};

const DRAW: Partial<Record<WallPattern, Draw>> = {
	[WallPattern.Stripes]: stripes,
	[WallPattern.Rhombus]: rhombus,
	[WallPattern.Damask]: damask,
	[WallPattern.Geometric]: geometric,
	[WallPattern.Linen]: linen,
	[WallPattern.Botanical]: botanical,
	[WallPattern.Concrete]: concrete,
	[WallPattern.Silk]: silk,
};

/**
 * Рисует плитку рисунка на холсте: фон — основной цвет, узор — `accent`.
 * false — у отделки нет рисунка (краска, картинка из библиотеки).
 */
export const drawFinishPattern = (
	ctx: CanvasRenderingContext2D,
	w: number,
	h: number,
	pattern: WallPattern,
	color: string,
	accent: string,
): boolean => {
	const draw = DRAW[pattern];
	if (!draw) return false;
	ctx.fillStyle = color;
	ctx.fillRect(0, 0, w, h);
	draw(ctx, w, h, accent);
	return true;
};

/** Ширина плитки образца в каталоге, px: в карточку влезает два раппорта, линии видны. */
const SWATCH_TILE_PX = 56;

const swatchTiles = new Map<string, string | null>();

/**
 * Плитка рисунка для образца в каталоге — data URL, ставится фоном с повтором.
 * null — рисунка нет. Одна картинка на сочетание «рисунок + два цвета».
 */
export const finishPatternTile = (
	pattern: WallPattern,
	color: string,
	accent: string,
): string | null => {
	const key = `${pattern}|${color}|${accent}`;
	const cached = swatchTiles.get(key);
	if (cached !== undefined) return cached;
	const canvas = document.createElement("canvas");
	canvas.width = SWATCH_TILE_PX;
	canvas.height = Math.round(SWATCH_TILE_PX * patternAspect(pattern));
	const ctx = canvas.getContext("2d");
	const tile =
		ctx && drawFinishPattern(ctx, canvas.width, canvas.height, pattern, color, accent)
			? canvas.toDataURL()
			: null;
	swatchTiles.set(key, tile);
	return tile;
};

/** Размер плитки образца на карточке — CSS `background-size`. */
export const finishPatternTileSize = (pattern: WallPattern): string =>
	`${SWATCH_TILE_PX}px ${Math.round(SWATCH_TILE_PX * patternAspect(pattern))}px`;
